import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSave, faCheckCircle, faTimes } from "@fortawesome/free-solid-svg-icons";
import "./EditTeam.css";
import { drivers, constructors, TOTAL_BUDGET } from '../data/teamData';
import { useAuth } from '../auth';
import { fantasyTeamService } from '../services/firestore';

const constructorBackgrounds = {
  'red_bull': '/images/redbull.png',
  'ferrari': '/images/ferrari.png',
  'mercedes': '/images/mercedes.png',
  'mclaren': '/images/mclaren.png',
  'aston_martin': '/images/astonmartin.png',
  'alpine': '/images/alpine.png',
  'williams': '/images/williams.png',
  'rb': '/images/visacashapp.png',
  'kick_sauber': '/images/kicksauber.png',
  'haas': '/images/haas.png'
};

const teamColors = {
  'red_bull': '#0600EF',
  'ferrari': '#E8002D',
  'mercedes': '#00D2BE',
  'mclaren': '#FF8700',
  'aston_martin': '#006F62',
  'alpine': '#0090FF',
  'williams': '#00A0DC',
  'rb': '#6692FF',
  'kick_sauber': '#52E252',
  'haas': '#B6BABD'
};

const EditTeam = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userTeam, setUserTeam] = useState(null);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [selectedConstructor, setSelectedConstructor] = useState(null);
  const [budget, setBudget] = useState(TOTAL_BUDGET);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [teamColor, setTeamColor] = useState('#E10600');
  const [backgroundImage, setBackgroundImage] = useState('');

  useEffect(() => {
    const fetchUserTeam = async () => {
      if (!user?.uid) {
        navigate('/login');
        return;
      }

      try {
        // Set team branding
        const selectedTeam = user.firestoreUser?.favoriteTeam;
        if (selectedTeam) {
          setBackgroundImage(constructorBackgrounds[selectedTeam] || '');
          setTeamColor(teamColors[selectedTeam] || '#E10600');
        }

        const teamData = await fantasyTeamService.getTeam(user.uid);
        
        if (!teamData) {
          setError("No team found. Please create a team first.");
          navigate('/create-team');
          return;
        }

        setUserTeam(teamData);
        
        // Map driver IDs to driver objects
        const teamDriverIds = [
          teamData.driver_1,
          teamData.driver_2,
          teamData.driver_3,
          teamData.driver_4,
        ];
        
        const teamDrivers = teamDriverIds
          .map(id => drivers.find(d => d.id === id))
          .filter(d => d !== undefined);
        
        setSelectedDrivers(teamDrivers);
        
        // Map constructor ID to constructor object
        const teamConstructor = constructors.find(c => c.id === teamData.constructor);
        setSelectedConstructor(teamConstructor || null);
      } catch (error) {
        console.error("Error fetching user team:", error);
        setError("Failed to load team data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserTeam();
  }, [user, navigate]);

  const calculateRemainingBudget = () => {
    const driversCost = selectedDrivers.reduce((total, driver) => {
      return total + driver.price;
    }, 0);

    const constructorCost = selectedConstructor ? selectedConstructor.price : 0;
    return (TOTAL_BUDGET - driversCost - constructorCost).toFixed(1);
  };

  const handleDriverSelect = (driver) => {
    if (selectedDrivers.find(d => d.id === driver.id)) {
      setSelectedDrivers(selectedDrivers.filter((d) => d.id !== driver.id));
    } else if (selectedDrivers.length < 4) {
      const newBudget = parseFloat(calculateRemainingBudget()) - driver.price;
      if (newBudget >= 0) {
        setSelectedDrivers([...selectedDrivers, driver]);
        setError("");
      } else {
        setError("Not enough budget!");
      }
    } else {
      setError("You can only select 4 drivers");
    }
  };

  const handleConstructorSelect = (constructor) => {
    setSelectedConstructor(constructor);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (selectedDrivers.length !== 4) {
      setError("Please select exactly 4 drivers");
      return;
    }

    if (!selectedConstructor) {
      setError("Please select a constructor");
      return;
    }

    if (!userTeam?.id) {
      setError("Team data not loaded");
      return;
    }

    const remainingBudget = parseFloat(calculateRemainingBudget());
    if (remainingBudget < 0) {
      setError("Team exceeds budget limit");
      return;
    }

    try {
      const updates = {
        driver_1: selectedDrivers[0].id,
        driver_2: selectedDrivers[1].id,
        driver_3: selectedDrivers[2].id,
        driver_4: selectedDrivers[3].id,
        constructor: selectedConstructor.id,
        budgetRemaining: remainingBudget,
      };

      // Validate before updating
      const validation = fantasyTeamService.validateTeam(updates);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      // Update team in Firestore
      await fantasyTeamService.updateTeam(userTeam.id, updates);
      
      console.log('✅ Team updated successfully');
      navigate("/dashboard");
    } catch (error) {
      console.error("Error updating team:", error);
      setError(error.message || "Failed to update team");
    }
  };

  const handleBack = () => {
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Loading your team...</p>
      </div>
    );
  }

  return (
    <div 
      className="edit-team-container"
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : '',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        '--team-color': teamColor
      }}
    >
      {/* Navigation - Match Dashboard */}
      <nav className="edit-team-nav">
        <button className="back-btn" onClick={handleBack} style={{ borderColor: teamColor, color: teamColor }}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back</span>
        </button>
        <h1 className="page-title">Edit Your Team</h1>
        <div className="nav-spacer"></div>
      </nav>

      <form onSubmit={handleSubmit} className="team-form">
        {/* Budget Info */}
        <div className="budget-bar">
          <div className="budget-card">
            <span className="budget-label">Total Budget</span>
            <span className="budget-value" style={{ color: teamColor }}>${TOTAL_BUDGET}M</span>
          </div>
          <div className="budget-divider"></div>
          <div className="budget-card">
            <span className="budget-label">Remaining</span>
            <span 
              className="budget-value"
              style={{ color: parseFloat(calculateRemainingBudget()) < 0 ? '#ff4444' : teamColor }}
            >
              ${calculateRemainingBudget()}M
            </span>
          </div>
          <div className="budget-divider"></div>
          <div className="budget-card">
            <span className="budget-label">Selected</span>
            <span className="budget-value" style={{ color: teamColor }}>
              {selectedDrivers.length}/4
            </span>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <FontAwesomeIcon icon={faTimes} />
            <span>{error}</span>
          </div>
        )}

        {/* Drivers Section */}
        <div className="form-section">
          <div className="section-header">
            <h2>Select Drivers</h2>
            <span className="section-subtitle">Choose exactly 4 drivers for your team</span>
          </div>
          <div className="drivers-grid">
            {drivers.map((driver) => {
              const isSelected = selectedDrivers.find(d => d.id === driver.id);
              return (
                <div
                  key={driver.id}
                  className={`driver-card ${isSelected ? "selected" : ""}`}
                  onClick={() => handleDriverSelect(driver)}
                  style={{
                    borderColor: isSelected ? teamColor : 'rgba(255, 255, 255, 0.1)'
                  }}
                >
                  {isSelected && (
                    <div className="selected-badge" style={{ backgroundColor: teamColor }}>
                      <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                  )}
                  <div className="driver-info">
                    <h3>{driver.name}</h3>
                    <p className="driver-team">{driver.team}</p>
                    <div className="driver-price">
                      <span className="price-label">Price</span>
                      <span className="price-value" style={{ color: teamColor }}>${driver.price}M</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Constructor Section */}
        <div className="form-section">
          <div className="section-header">
            <h2>Select Constructor</h2>
            <span className="section-subtitle">Choose your team's constructor</span>
          </div>
          <div className="constructors-grid">
            {constructors.map((constructor) => {
              const isSelected = selectedConstructor?.id === constructor.id;
              return (
                <div
                  key={constructor.id}
                  className={`constructor-card ${isSelected ? "selected" : ""}`}
                  onClick={() => handleConstructorSelect(constructor)}
                  style={{
                    borderColor: isSelected ? teamColor : 'rgba(255, 255, 255, 0.1)'
                  }}
                >
                  {isSelected && (
                    <div className="selected-badge" style={{ backgroundColor: teamColor }}>
                      <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                  )}
                  <div className="constructor-info">
                    <h3>{constructor.name}</h3>
                    <div className="constructor-price">
                      <span className="price-label">Price</span>
                      <span className="price-value" style={{ color: teamColor }}>${constructor.price}M</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit Button */}
        <button 
          type="submit" 
          className="submit-btn"
          style={{ backgroundColor: teamColor }}
        >
          <FontAwesomeIcon icon={faSave} />
          <span>Save Changes</span>
        </button>
      </form>
    </div>
  );
};

export default EditTeam; 